/**
 * A typed TypeScript port of the ZzFX sample generator (`buildSamples`, ZzFX 1.3.x), used to pre-render the
 * M1 sound bank at init (plan G11, D-016). Only the generator is ported: playback is our own mixer.
 *
 * ZzFX - Zuper Zmall Zound Zynth - Micro Edition
 * MIT License - Copyright 2019 Frank Force
 * https://github.com/KilledByAPixel/ZzFX
 *
 * Changes from the original: typed parameters, the sample rate is an argument (the original hard-codes
 * 44,100 Hz), the randomness source is injected (so a parameter array renders deterministically), and the
 * output is a preallocated `Float32Array` instead of a growing JS array. The synthesis maths is unchanged.
 */

/**
 * A ZzFX parameter array as the ZzFX designer exports it (missing trailing values take the defaults):
 * `[volume, randomness, frequency, attack, sustain, release, shape, shapeCurve, slide, deltaSlide,
 *   pitchJump, pitchJumpTime, repeatTime, noise, modulation, bitCrush, delay, sustainVolume, decay,
 *   tremolo, filter]`.
 */
export type ZzfxParams = readonly number[]

/** Number of ZzFX parameters. */
export const ZZFX_PARAM_COUNT = 21

/** ZzFX's own master volume (`zzfxV`): parameter arrays from the designer are balanced against it. */
export const ZZFX_VOLUME = 0.3

/** The original's sample rate; parameters are in seconds and hertz, so any rate renders the same sound. */
export const ZZFX_SAMPLE_RATE = 44100

/** Defaults of every parameter, in order. */
const DEFAULTS: readonly number[] = [1, 0.05, 220, 0, 0, 0.1, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0]

function param(p: ZzfxParams, i: number): number {
  const v = i < p.length ? p[i] : undefined
  return v === undefined || Number.isNaN(v) ? DEFAULTS[i] : v
}

function sign(v: number): number {
  return v < 0 ? -1 : 1
}

/** Length in samples of `params` rendered at `sampleRate`. */
export function zzfxLength(params: ZzfxParams, sampleRate: number = ZZFX_SAMPLE_RATE): number {
  const attack = param(params, 3) * sampleRate + 9
  const decay = param(params, 18) * sampleRate
  const sustain = param(params, 4) * sampleRate
  const release = param(params, 5) * sampleRate
  const delay = param(params, 16) * sampleRate
  return (attack + decay + sustain + release + delay) | 0
}

/**
 * Renders `params` to mono samples in -1..1-ish (volume-scaled, not clamped: the mixer's limiter handles
 * peaks). `random` returns [0, 1) and is called once (the frequency randomness); pass a seeded generator for
 * a deterministic result. Init-time only: allocates the output.
 */
export function renderZzfx(
  params: ZzfxParams,
  sampleRate: number = ZZFX_SAMPLE_RATE,
  random: () => number = Math.random,
): Float32Array {
  const PI2 = Math.PI * 2
  const abs = Math.abs
  let volume = param(params, 0)
  const randomness = param(params, 1)
  let frequency = param(params, 2)
  let attack = param(params, 3)
  let sustain = param(params, 4)
  let release = param(params, 5)
  const shape = param(params, 6)
  const shapeCurve = param(params, 7)
  let slide = param(params, 8)
  let deltaSlide = param(params, 9)
  let pitchJump = param(params, 10)
  let pitchJumpTime = param(params, 11)
  let repeatTime = param(params, 12)
  const noise = param(params, 13)
  let modulation = param(params, 14)
  const bitCrush = param(params, 15)
  let delay = param(params, 16)
  const sustainVolume = param(params, 17)
  let decay = param(params, 18)
  const tremolo = param(params, 19)
  const filter = param(params, 20)

  // init parameters
  const startSlide = (slide *= (500 * PI2) / sampleRate / sampleRate)
  let startFrequency = (frequency *= ((1 + randomness * 2 * random() - randomness) * PI2) / sampleRate)
  let t = 0
  let tm = 0
  let i = 0
  let j = 1
  let r = 0
  let c = 0
  let s = 0
  let f: number

  // biquad LP/HP filter
  const quality = 2
  const w = (PI2 * abs(filter) * 2) / sampleRate
  const cos = Math.cos(w)
  const alpha = Math.sin(w) / 2 / quality
  const a0 = 1 + alpha
  const a1 = (-2 * cos) / a0
  const a2 = (1 - alpha) / a0
  const b0 = (1 + sign(filter) * cos) / 2 / a0
  const b1 = -(sign(filter) + cos) / a0
  const b2 = b0
  let x2 = 0
  let x1 = 0
  let y2 = 0
  let y1 = 0

  // scale by sample rate
  attack = attack * sampleRate + 9 // minimum attack to prevent pop
  decay *= sampleRate
  sustain *= sampleRate
  release *= sampleRate
  delay *= sampleRate
  deltaSlide *= (500 * PI2) / sampleRate ** 3
  modulation *= PI2 / sampleRate
  pitchJump *= PI2 / sampleRate
  pitchJumpTime *= sampleRate
  repeatTime = (repeatTime * sampleRate) | 0
  volume *= ZZFX_VOLUME

  const length = (attack + decay + sustain + release + delay) | 0
  const b = new Float32Array(length > 0 ? length : 0)
  const crush = (bitCrush * 100) | 0

  // generate waveform
  for (; i < length; b[i++] = s * volume) {
    // bit crush (`c % 0` is NaN, so a crush of 0 recomputes every sample, as in the original)
    if (!(++c % crush)) {
      s = shape
        ? shape > 1
          ? shape > 2
            ? shape > 3
              ? Math.sin(t ** 3) // 4 noise
              : Math.max(Math.min(Math.tan(t), 1), -1) // 3 tan
            : 1 - (((((2 * t) / PI2) % 2) + 2) % 2) // 2 saw
          : 1 - 4 * abs(Math.round(t / PI2) - t / PI2) // 1 triangle
        : Math.sin(t) // 0 sin

      s =
        (repeatTime ? 1 - tremolo + tremolo * Math.sin((PI2 * i) / repeatTime) : 1) * // tremolo
        sign(s) *
        abs(s) ** shapeCurve * // curve
        (i < attack
          ? i / attack // attack
          : i < attack + decay
            ? 1 - ((i - attack) / decay) * (1 - sustainVolume) // decay falloff
            : i < attack + decay + sustain
              ? sustainVolume // sustain volume
              : i < length - delay
                ? ((length - i - delay) / release) * sustainVolume // release falloff
                : 0) // post release

      s = delay
        ? s / 2 + (delay > i ? 0 : ((i < length - delay ? 1 : (length - i) / delay) * b[(i - delay) | 0]) / 2 / volume) // sample delay
        : s

      if (filter) {
        // apply filter
        const y = b2 * x2 + b1 * x1 + b0 * s - a2 * y2 - a1 * y1
        x2 = x1
        x1 = s
        y2 = y1
        y1 = y
        s = y
      }
    }

    f = (frequency += slide += deltaSlide) * Math.cos(modulation * tm++) // frequency + modulation
    t += f - f * noise * (1 - (((Math.sin(i) + 1) * 1e9) % 2)) // noise

    if (j && ++j > pitchJumpTime) {
      // pitch jump
      frequency += pitchJump
      startFrequency += pitchJump
      j = 0
    }

    if (repeatTime && !(++r % repeatTime)) {
      // repeat
      frequency = startFrequency
      slide = startSlide
      j = j || 1
    }
  }

  return b
}
